import React, { useState, useEffect } from 'react';
import {NPMPackageInfo} from "../types";

interface NPMPackagesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onAddPackage: (packageInfo: NPMPackageInfo) => Promise<string | null>;
    onRemovePackage: (packageName: string) => Promise<string | null>;
    currentPackages: NPMPackageInfo[];
    // NEW: Add these props for package.json parsing
    project?: any;
    userActor?: any;
    userPrincipal?: any;
    currentVersionId?: string | null;
}

const POPULAR_PACKAGES = [
    // Core React packages (required defaults)
    {
        name: 'react',
        description: 'A JavaScript library for building user interfaces',
        version: '^18.2.0',
        dependencyType: 'dependencies'
    },
    {
        name: 'react-dom',
        description: 'React package for working with the DOM',
        version: '^18.2.0',
        dependencyType: 'dependencies'
    },

    // TypeScript definitions (required defaults)
    {
        name: '@types/react',
        description: 'TypeScript definitions for React',
        version: '^18.2.15',
        dependencyType: 'devDependencies'
    },
    {
        name: '@types/react-dom',
        description: 'TypeScript definitions for React DOM',
        version: '^18.2.7',
        dependencyType: 'devDependencies'
    },

    // Build tools (required defaults)
    {
        name: '@vitejs/plugin-react',
        description: 'Official Vite plugin for React',
        version: '^4.2.1',
        dependencyType: 'devDependencies'
    },
    {
        name: 'typescript',
        description: 'TypeScript is a language for application scale JavaScript development',
        version: '^5.1.6',
        dependencyType: 'devDependencies'
    },
    {
        name: 'vite',
        description: 'Next generation frontend tooling',
        version: '^5.0.8',
        dependencyType: 'devDependencies'
    },

    // ICP/dfinity packages - UPDATED VERSIONS
    {
        name: '@dfinity/agent',
        description: 'JavaScript/TypeScript library for the Internet Computer',
        version: '^2.4.0',
        dependencyType: 'dependencies'
    },
    {
        name: '@dfinity/candid',
        description: 'JavaScript/TypeScript Candid library',
        version: '^2.4.1',
        dependencyType: 'dependencies'
    },
    {
        name: '@dfinity/principal',
        description: 'JavaScript/TypeScript Principal library',
        version: '^2.4.1',
        dependencyType: 'dependencies'
    },
    {
        name: '@dfinity/auth-client',
        description: 'JavaScript/TypeScript authentication library for Internet Computer',
        version: '^2.4.0',
        dependencyType: 'dependencies'
    },
    {
        name: '@dfinity/identity',
        description: 'JavaScript/TypeScript identity library for Internet Computer',
        version: '^2.4.0',
        dependencyType: 'dependencies'
    },
    {
        name: '@dfinity/assets',
        description: 'JavaScript/TypeScript assets library for Internet Computer',
        version: '^2.4.0',
        dependencyType: 'dependencies'
    },
    {
        name: '@dfinity/utils',
        description: 'JavaScript/TypeScript utilities for Internet Computer',
        version: '^2.10.0',
        dependencyType: 'dependencies'
    },
    {
        name: '@dfinity/ic-management',
        description: 'JavaScript/TypeScript IC management library',
        version: '^6.0.4',
        dependencyType: 'dependencies'
    },

    // CSS and styling tools
    {
        name: 'tailwindcss',
        description: 'A utility-first CSS framework',
        version: '^3.3.0',
        dependencyType: 'devDependencies'
    },
    {
        name: 'autoprefixer',
        description: 'PostCSS plugin to parse CSS and add vendor prefixes',
        version: '^10.4.0',
        dependencyType: 'devDependencies'
    },
    {
        name: 'postcss',
        description: 'A tool for transforming CSS with JavaScript',
        version: '^8.4.0',
        dependencyType: 'devDependencies'
    },

    // Development tools
    {
        name: 'prettier',
        description: 'An opinionated code formatter',
        version: '^3.0.0',
        dependencyType: 'devDependencies'
    },
    {
        name: 'eslint',
        description: 'An AST-based pattern checker for JavaScript',
        version: '^8.0.0',
        dependencyType: 'devDependencies'
    }
] as const;

const NPMPackagesDialog: React.FC<NPMPackagesDialogProps> = ({
                                                                 isOpen,
                                                                 onClose,
                                                                 onAddPackage,
                                                                 onRemovePackage,
                                                                 currentPackages,
                                                                 project,
                                                                 userActor,
                                                                 userPrincipal,
                                                                 currentVersionId
                                                             }) => {
    const [activeTab, setActiveTab] = useState<'popular' | 'custom' | 'installed'>('popular');
    const [packageName, setPackageName] = useState('');
    const [packageVersion, setPackageVersion] = useState('');
    const [dependencyType, setDependencyType] = useState<'dependencies' | 'devDependencies' | 'peerDependencies'>('dependencies');
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    // NEW: Parse package.json and sync missing packages when dialog opens
    useEffect(() => {
        if (isOpen && project && userActor && userPrincipal) {
            syncPackagesFromPackageJson();
        }
    }, [isOpen, project?.id, userActor, userPrincipal, currentVersionId]);

    // Log when currentPackages changes
    useEffect(() => {
        console.log('[NPMDialog] currentPackages changed:', currentPackages.map(p => p.name));
    }, [currentPackages]);

    const syncPackagesFromPackageJson = async () => {
        try {
            setIsLoading(true);
            console.log('[NPMDialog] Starting package.json sync...');

            const versionParam = currentVersionId ? [currentVersionId] : [];

            // Get all project files
            const filesResult = await userActor.getProjectFiles(
                userPrincipal,
                project.id,
                versionParam
            );

            if (!('ok' in filesResult)) {
                console.warn('[NPMDialog] Failed to get project files:', filesResult.err);
                return;
            }

            // Find package.json file
            const packageJsonFile = filesResult.ok.find((file: any) =>
                file.fileName === 'package.json'
            );

            if (!packageJsonFile || !packageJsonFile.content) {
                console.log('[NPMDialog] No package.json found, skipping sync');
                return;
            }

            // Extract package.json content
            let packageJsonContent = '';
            if (typeof packageJsonFile.content === 'string') {
                packageJsonContent = packageJsonFile.content;
            } else if (Array.isArray(packageJsonFile.content) && packageJsonFile.content.length > 0) {
                const contentItem = packageJsonFile.content[0];
                if (contentItem && typeof contentItem === 'object' && 'Text' in contentItem) {
                    packageJsonContent = contentItem.Text;
                }
            } else if (packageJsonFile.content && typeof packageJsonFile.content === 'object' && 'Text' in packageJsonFile.content) {
                packageJsonContent = packageJsonFile.content.Text;
            }

            if (!packageJsonContent) {
                console.log('[NPMDialog] Empty package.json content, skipping sync');
                return;
            }

            // Parse package.json
            let packageJson;
            try {
                packageJson = JSON.parse(packageJsonContent);
            } catch (parseError) {
                console.error('[NPMDialog] Failed to parse package.json:', parseError);
                return;
            }

            console.log('[NPMDialog] Parsed package.json successfully');

            // Extract all dependencies
            const allDependencies = {
                ...packageJson.dependencies || {},
                ...packageJson.devDependencies || {},
                ...packageJson.peerDependencies || {}
            };

            console.log('[NPMDialog] Found dependencies in package.json:', Object.keys(allDependencies));
            console.log('[NPMDialog] Current backend packages:', currentPackages.map(p => p.name));

            // Find packages in package.json that aren't in backend
            const missingPackages: NPMPackageInfo[] = [];

            Object.entries(allDependencies).forEach(([name, version]) => {
                const existsInBackend = currentPackages.some(pkg => pkg.name === name);

                if (!existsInBackend) {
                    // Determine dependency type
                    let dependencyType: 'dependencies' | 'devDependencies' | 'peerDependencies' = 'dependencies';

                    if (packageJson.devDependencies && name in packageJson.devDependencies) {
                        dependencyType = 'devDependencies';
                    } else if (packageJson.peerDependencies && name in packageJson.peerDependencies) {
                        dependencyType = 'peerDependencies';
                    }

                    missingPackages.push({
                        name,
                        version: version as string,
                        dependencyType
                    });
                }
            });

            if (missingPackages.length > 0) {
                console.log('[NPMDialog] Found missing packages, syncing to backend:', missingPackages.map(p => p.name));

                // Add missing packages to backend one by one
                for (const pkg of missingPackages) {
                    try {
                        console.log(`[NPMDialog] Adding missing package: ${pkg.name}@${pkg.version}`);
                        await onAddPackage(pkg);
                    } catch (error) {
                        console.error(`[NPMDialog] Failed to sync package ${pkg.name}:`, error);
                    }
                }

                console.log('[NPMDialog] Package sync completed');
            } else {
                console.log('[NPMDialog] No missing packages found, already in sync');
            }

        } catch (error) {
            console.error('[NPMDialog] Error during package sync:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddCustomPackage = async () => {
        try {
            if (!packageName) {
                throw new Error('Package name is required');
            }

            if (!packageVersion) {
                throw new Error('Package version is required');
            }

            const packageInfo: NPMPackageInfo = {
                name: packageName,
                version: packageVersion,
                dependencyType
            };

            await onAddPackage(packageInfo);

            // Reset form
            setPackageName('');
            setPackageVersion('');
            setDependencyType('dependencies');
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to add package');
        }
    };

    // Use currentPackages directly for display (source of truth from parent)
    const displayPackages = currentPackages;
    const sortedDisplayPackages = [...displayPackages].sort((a, b) => a.name.localeCompare(b.name));

    const styles = {
        overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
        },
        content: {
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '24px',
            width: '600px',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        title: {
            fontSize: '24px',
            marginBottom: '24px',
            color: '#4B5320',
            fontWeight: '600',
        },
        tabs: {
            display: 'flex',
            marginBottom: '24px',
            borderBottom: '1px solid #ddd',
        },
        tab: {
            padding: '12px 24px',
            cursor: 'pointer',
            borderBottom: '2px solid transparent',
            color: '#666',
            fontSize: '16px',
        },
        activeTab: {
            borderBottom: '2px solid #8B4513',
            color: '#8B4513',
            fontWeight: '500',
        },
        packageList: {
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
        },
        packageItem: {
            padding: '16px',
            borderRadius: '6px',
            backgroundColor: '#f8f8f8',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        packageInfo: {
            flex: 1,
        },
        packageName: {
            fontWeight: '500',
            color: '#4B5320',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        packageDescription: {
            fontSize: '14px',
            color: '#666',
            marginTop: '4px',
        },
        dependencyType: {
            fontSize: '12px',
            padding: '2px 6px',
            borderRadius: '4px',
            backgroundColor: '#e5e7eb',
            color: '#374151',
            marginLeft: '8px',
        },
        formGroup: {
            marginBottom: '16px',
        },
        label: {
            display: 'block',
            marginBottom: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: '#4B5320',
        },
        input: {
            width: '100%',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '14px',
        },
        select: {
            width: '100%',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #ddd',
            fontSize: '14px',
            backgroundColor: '#fff',
        },
        button: {
            padding: '8px 16px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
        },
        addButton: {
            backgroundColor: '#8B4513',
            color: 'white',
        },
        removeButton: {
            backgroundColor: '#dc3545',
            color: 'white',
        },
        footer: {
            marginTop: '24px',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
        },
        errorText: {
            color: '#dc3545',
            fontSize: '14px',
            marginTop: '8px',
        },
        emptyState: {
            textAlign: 'center',
            padding: '40px 20px',
            color: '#666',
            fontSize: '16px',
        },
        packageCount: {
            fontSize: '14px',
            color: '#666',
            marginBottom: '16px',
            fontStyle: 'italic',
        },
        loadingIndicator: {
            fontSize: '14px',
            color: '#8B4513',
            fontStyle: 'italic',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
        }
    } as const;

    if (!isOpen) return null;

    return (
        <div style={styles.overlay as any}>
            <div style={styles.content as any}>
                <h2 style={styles.title}>NPM Packages</h2>

                {isLoading && (
                    <div style={styles.loadingIndicator}>
                        <span>🔄</span>
                        Syncing packages from package.json...
                    </div>
                )}

                <div style={styles.tabs}>
                    <div
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'popular' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('popular')}
                    >
                        Popular Packages
                    </div>
                    <div
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'custom' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('custom')}
                    >
                        Add Custom Package
                    </div>
                    <div
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'installed' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('installed')}
                    >
                        Installed Packages ({displayPackages.length})
                    </div>
                </div>

                {activeTab === 'popular' && (
                    <div style={styles.packageList}>
                        {POPULAR_PACKAGES.map((pkg) => {
                            const isInstalled = displayPackages.some(p => p.name === pkg.name);

                            return (
                                <div key={pkg.name} style={styles.packageItem}>
                                    <div style={styles.packageInfo}>
                                        <div style={styles.packageName}>
                                            {pkg.name}
                                            <span style={styles.dependencyType}>
                                                {pkg.dependencyType}
                                            </span>
                                        </div>
                                        <div style={styles.packageDescription}>{pkg.description}</div>
                                        <div style={styles.packageDescription}>Version: {pkg.version}</div>
                                    </div>
                                    <button
                                        onClick={async () => {
                                            console.log('[NPMDialog] Button clicked for:', pkg.name, 'isInstalled:', isInstalled);
                                            try {
                                                if (isInstalled) {
                                                    console.log('[NPMDialog] Removing package:', pkg.name);
                                                    const result = await onRemovePackage(pkg.name);
                                                    console.log('[NPMDialog] Remove result:', result);
                                                } else {
                                                    console.log('[NPMDialog] Adding package:', pkg.name);
                                                    const result = await onAddPackage({
                                                        name: pkg.name,
                                                        version: pkg.version,
                                                        dependencyType: pkg.dependencyType
                                                    });
                                                    console.log('[NPMDialog] Add result:', result);
                                                }
                                            } catch (error) {
                                                console.error('[NPMDialog] Operation error:', error);
                                            }
                                        }}
                                        style={{
                                            ...styles.button,
                                            ...(isInstalled ? styles.removeButton : styles.addButton)
                                        }}
                                        disabled={isLoading}
                                    >
                                        {isInstalled ? 'Remove' : 'Add'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}

                {activeTab === 'custom' && (
                    <div style={{ padding: '20px' }}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Package Name</label>
                            <input
                                type="text"
                                placeholder="e.g. lodash"
                                value={packageName}
                                onChange={(e) => setPackageName(e.target.value)}
                                style={styles.input}
                                disabled={isLoading}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Version</label>
                            <input
                                type="text"
                                placeholder="e.g. ^4.0.0"
                                value={packageVersion}
                                onChange={(e) => setPackageVersion(e.target.value)}
                                style={styles.input}
                                disabled={isLoading}
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.label}>Dependency Type</label>
                            <select
                                value={dependencyType}
                                onChange={(e) => setDependencyType(e.target.value as any)}
                                style={styles.select}
                                disabled={isLoading}
                            >
                                <option value="dependencies">dependencies</option>
                                <option value="devDependencies">devDependencies</option>
                                <option value="peerDependencies">peerDependencies</option>
                            </select>
                        </div>

                        <button
                            style={{...styles.button, ...styles.addButton}}
                            onClick={handleAddCustomPackage}
                            disabled={isLoading}
                        >
                            Add Package
                        </button>
                        {error && <div style={styles.errorText}>{error}</div>}
                    </div>
                )}

                {activeTab === 'installed' && (
                    <div>
                        {displayPackages.length === 0 ? (
                            <div style={styles.emptyState}>
                                <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
                                <div>No packages installed yet</div>
                                <div style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
                                    Use the "Popular Packages" or "Add Custom Package" tabs to get started
                                </div>
                            </div>
                        ) : (
                            <>
                                <div style={styles.packageCount}>
                                    Showing {displayPackages.length} installed package{displayPackages.length !== 1 ? 's' : ''}
                                </div>
                                <div style={styles.packageList}>
                                    {sortedDisplayPackages.map((pkg) => (
                                        <div key={pkg.name} style={styles.packageItem}>
                                            <div style={styles.packageInfo}>
                                                <div style={styles.packageName}>
                                                    {pkg.name}
                                                    <span style={styles.dependencyType}>
                                                        {pkg.dependencyType}
                                                    </span>
                                                </div>
                                                <div style={styles.packageDescription}>Version: {pkg.version}</div>
                                            </div>
                                            <button
                                                onClick={async () => {
                                                    console.log('[NPMDialog] Removing package:', pkg.name);
                                                    try {
                                                        const result = await onRemovePackage(pkg.name);
                                                        console.log('[NPMDialog] Remove result:', result);
                                                    } catch (error) {
                                                        console.error('[NPMDialog] Remove error:', error);
                                                    }
                                                }}
                                                style={{
                                                    ...styles.button,
                                                    ...styles.removeButton
                                                }}
                                                disabled={isLoading}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                <div style={styles.footer}>
                    <button
                        style={{...styles.button, backgroundColor: '#f3f4f6', color: '#374151'}}
                        onClick={onClose}
                        disabled={isLoading}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NPMPackagesDialog;